/**
 * Co-located TanStack Query hooks for the rep "My leads" surface (S5). Mutations
 * carry the entity `version` for the optimistic lock; a 409 (`ConflictError`) is
 * surfaced to the caller so the screen can reload + warn rather than overwrite.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { repLeadsApi } from './api';
import type { RepTask } from './types';

export const repKeys = {
  all: ['com', 'rep', 'leads'] as const,
  list: () => ['com', 'rep', 'leads', 'list'] as const,
  detail: (taskId: string) => ['com', 'rep', 'leads', 'detail', taskId] as const,
};

export function useRepTasksQuery() {
  return useQuery<RepTask[]>({
    queryKey: repKeys.list(),
    queryFn: () => repLeadsApi.list(),
  });
}

export function useRepTaskQuery(taskId: string | undefined) {
  return useQuery<RepTask>({
    queryKey: repKeys.detail(taskId ?? ''),
    queryFn: () => repLeadsApi.detail(taskId as string),
    enabled: Boolean(taskId),
  });
}

function useTaskInvalidation() {
  const qc = useQueryClient();
  return (task: RepTask) => {
    qc.setQueryData(repKeys.detail(task.id), task);
    void qc.invalidateQueries({ queryKey: repKeys.list() });
  };
}

export function useMarkWorkedMutation() {
  const onTask = useTaskInvalidation();
  return useMutation<RepTask, unknown, { taskId: string; version: number }>({
    mutationFn: ({ taskId, version }) => repLeadsApi.markWorked(taskId, version),
    onSuccess: onTask,
  });
}

export function useEscalateMutation() {
  const onTask = useTaskInvalidation();
  return useMutation<RepTask, unknown, { taskId: string; version: number }>({
    mutationFn: ({ taskId, version }) => repLeadsApi.escalate(taskId, version),
    onSuccess: onTask,
  });
}
